import { useState, useEffect, useCallback, useRef } from "react";
import "../../../styles/pages/ouber.scss";
import { Space, Button, Typography, Alert, Input, Row, Col, InputNumber, Select, Modal } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  DeleteOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import PvTable from "../../../components/Table/PvTable";
import DataView from "../../../components/DataView/DataView";
import PvCardList from "../../../components/DataView/PvCardList";
import AddVehicleModal from "../../../components/Ouber/modals/admin/AdminAddVehicleModal";
import AdminViewVehicleModal from "../../../components/Ouber/modals/admin/AdminViewVehicleModal";
import { showErrorToast, showSuccessToast } from "../../../components/Ouber/ToastNotification";
import { useOuberService } from "../../../services";

/**
 * The purpose of this page is to manage the fleet of vehicles, 
 * track plate numbers, and monitor their passenger capacities.
 */
const ManageVehiclePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const { dataView } = useSelector((state) => state.generalSlice);
  const { details: profile, account_types = [] } = useSelector((state) => state.accountDetailSlice);
  const isSuperAdmin = profile?.account_type === "Super Admin" || account_types.includes("superadmin") || account_types.includes("xadmin");

  const [filterModel, setFilterModel] = useState("");
  const [filterPlate, setFilterPlate] = useState("");
  const [filterCapacity, setFilterCapacity] = useState(null);
  const [filterType, setFilterType] = useState(null);


  const { service: ouberService, loading } = useOuberService();
  const [data, setData] = useState([]);

  // Retrieves the vehicle roster from the backend with filters for model, plate, capacity, and type
  const fetchVehicles = useCallback(async () => {
    try {
      const params = {
        search_model: filterModel || undefined,
        plate_number: filterPlate || undefined,
        min_capacity: filterCapacity !== null ? filterCapacity : undefined,
        vehicle_type: filterType || undefined,
      };
      const response = await ouberService.getVehicles(params);
      
      const vehiclesList = Array.isArray(response) ? response : (response?.data || []);

      const vehicles = vehiclesList.map(v => ({ ...v, key: v.vehicle_id.toString(), id: v.vehicle_id.toString() }));
      
      // Formats raw vehicle data into a consistent UI-friendly object structure
      setData(vehicles);
    } catch (err) {
      showErrorToast("Fetch Error", "Failed to load vehicles.");
    }
  }, [filterModel, filterPlate, filterCapacity, filterType, ouberService]);

  const fetchVehiclesRef = useRef(fetchVehicles);
  useEffect(() => {
    fetchVehiclesRef.current = fetchVehicles;
  }, [fetchVehicles]);

  // Implements a debounced fetch to handle live search filtering for the vehicle list
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchVehiclesRef.current();
    }, 500);
    return () => clearTimeout(handler);
  }, [filterModel, filterPlate, filterCapacity, filterType]);



  const filteredData = data;

  const handleEditVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsModalOpen(true);
  };

  const handleViewVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsViewModalOpen(true);
  };

  const handleCreateVehicle = () => {
    setSelectedVehicle(null);
    setIsModalOpen(true);
  };

  const handleDeleteVehicle = (vehicle) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this vehicle?',
      content: `This will permanently remove ${vehicle.vehicle_model} (${vehicle.plate_number}) from the registry.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await ouberService.deleteVehicle(vehicle.id);
          showSuccessToast("Success", "Vehicle deleted successfully.");
          fetchVehicles();
        } catch (e) {
          showErrorToast("Error", "Failed to delete vehicle. It might be assigned to a trip.");
        }
      }
    });
  };

  const handleSaveVehicle = async (values) => {
    try {
      if (selectedVehicle) {
        await ouberService.updateVehicle(selectedVehicle.id, values);
        showSuccessToast("Success", "Vehicle updated successfully.");
      } else {
        await ouberService.createVehicle(values);
        showSuccessToast("Success", "Vehicle created successfully.");
      }
      setIsModalOpen(false);
      fetchVehicles();
    } catch (err) {
      showErrorToast("Error", "Failed to save vehicle.");
    }
  };

  const columns = [
    { title: "Vehicle ID", dataIndex: "id", key: "id", align: "center" },
    { title: "Vehicle Model", dataIndex: "vehicle_model", key: "vehicle_model", align: "center" },
    { title: "Vehicle Type", dataIndex: "vehicle_type", key: "vehicle_type", align: "center" },
    {
      title: "Plate Number",
      dataIndex: "plate_number",
      key: "plate_number",
      align: "center",
    },
    {
      title: "Capacity",
      dataIndex: "max_passenger_capacity",
      key: "max_passenger_capacity",
      align: "center",
      render: (capacity) => `${capacity}`
    },
    {
      title: "Next Assignment",
      key: "next_assignment",
      align: "center",
      render: (_, record) => {
        const next = record.next_assignment;
        if (!next) return <span style={{ color: "#bfbfbf", fontSize: "12px" }}>None Scheduled</span>;
        
        return (
          <div style={{ fontSize: "12px", lineHeight: "1.2" }}>
            <div style={{ fontWeight: "bold", color: "#862633" }}>
              {next.trip_date ? next.trip_date.split('T')[0] : 'N/A'}
            </div>
            <div style={{ color: "#595959" }}>{next.dropoff_location || next.pickup_venue}</div>
          </div>
        );
      }
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <EyeOutlined
            style={{ fontSize: "18px", cursor: "pointer", color: "#1890ff" }}
            onClick={() => handleViewVehicle(record)}
          />
          <EditOutlined
            style={{ fontSize: "18px", cursor: "pointer", color: "#52c41a" }}
            onClick={() => handleEditVehicle(record)}
          />
          {isSuperAdmin && (
            <DeleteOutlined
              style={{ fontSize: "18px", cursor: "pointer", color: "#f5222d" }}
              onClick={() => handleDeleteVehicle(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="ouber-page-container">
      <Space className="ouber-page-header">
        <Typography.Title level={3} className="ouber-page-title">
          Manage Vehicles
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          className="ouber-btn-forest"
          onClick={handleCreateVehicle}
        >
          Create Vehicle
        </Button>
      </Space>

      <Alert
        className="ouber-alert"
        message={<span className="ouber-alert-title">Vehicle Registry</span>}
        description={
          <span className="ouber-alert-desc">
            View, edit, and manage system-wide vehicles, including their plate numbers, minimum capacities, and next trip assignments.
          </span>
        }
        type="info"
        showIcon
      />

      <div className="ouber-filter-container">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Vehicle Model" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterModel}
              onChange={(e) => setFilterModel(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Plate Number" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterPlate}
              onChange={(e) => setFilterPlate(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Type"
              style={{ width: "100%" }}
              allowClear
              value={filterType}
              onChange={(val) => setFilterType(val)}
              options={[
                { value: "Car Rental", label: "Car Rental" },
                { value: "Carpool", label: "Carpool" },
                { value: "University Owned", label: "University Owned" },
                { value: "Outsourced", label: "Outsourced" },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Min Capacity"
              min={1}
              precision={0}
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              value={filterCapacity}
              onChange={(val) => setFilterCapacity(val)}
            />
          </Col>
        </Row>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "12px" }}>
        <DataView />
        <Button 
          icon={<SyncOutlined />} 
          onClick={fetchVehicles} 
          loading={loading}
          className="ouber-btn-primary"
          style={{ marginBottom: 0 }}
        >
          Sync Data
        </Button>
      </div>

      {dataView === "table" ? (
        <PvTable
          data={filteredData}
          columns={columns}
          loading={loading}
          onRowClick={() => {}}
        />
      ) : (
        <PvCardList
          data={filteredData}
          columns={columns}
          onRowClick={() => {}}
          loading={loading}
          maxActiveRow={filteredData.length}
        />
      )}

      <AddVehicleModal 
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onSave={handleSaveVehicle}
        initialValues={selectedVehicle}
      />

      <AdminViewVehicleModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        vehicle={selectedVehicle}
      />
    </div>
  );
};

export default ManageVehiclePage;
